import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, FileText, Search, Edit, Plus, Check, Loader2 } from 'lucide-react';
export interface ReasoningStep {
  id: string;
  title: string;
  description: string;
  action?: 'read' | 'search' | 'analyze' | 'create' | 'complete';
  details?: string[];
  duration?: number;
  status: 'pending' | 'active' | 'complete';
}
interface ReasoningDisplayProps {
  steps: ReasoningStep[];
  isThinking?: boolean;
}
export function ReasoningDisplay({
  steps,
  isThinking = false
}: ReasoningDisplayProps) {
  const [visibleDetailIndices, setVisibleDetailIndices] = useState<number[]>([]);
  const getActionIcon = (action?: string) => {
    switch (action) {
      case 'read':
        return <FileText size={14} data-id="element-542" />;
      case 'search':
        return <Search size={14} data-id="element-543" />;
      case 'analyze':
        return <Edit size={14} data-id="element-544" />;
      case 'create':
        return <Plus size={14} data-id="element-545" />;
      case 'complete':
        return <Check size={14} data-id="element-546" />;
      default:
        return null;
    }
  };
  const getActionColor = (action?: string) => {
    switch (action) {
      case 'read':
        return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'search':
        return 'text-indigo-700 bg-indigo-50 border-indigo-200';
      case 'analyze':
        return 'text-amber-700 bg-amber-50 border-amber-200';
      case 'create':
        return 'text-emerald-700 bg-emerald-50 border-emerald-200';
      case 'complete':
        return 'text-teal-700 bg-teal-50 border-teal-200';
      default:
        return 'text-slate-700 bg-slate-50 border-slate-200';
    }
  };
  const activeStep = steps.find(s => s.status === 'active');
  const activeStepIndex = steps.findIndex(s => s.status === 'active');
  const completedSteps = steps.filter(s => s.status === 'complete').length;
  // Progressive reveal of details for active step
  useEffect(() => {
    if (!activeStep?.details) {
      setVisibleDetailIndices([]);
      return;
    }
    // Reset when step changes
    setVisibleDetailIndices([]);
    // Progressively reveal each detail
    const timers: NodeJS.Timeout[] = [];
    activeStep.details.forEach((_, idx) => {
      const timer = setTimeout(() => {
        setVisibleDetailIndices(prev => [...prev, idx]);
      }, idx * 400); // 400ms delay between each detail
      timers.push(timer);
    });
    return () => timers.forEach(clearTimeout);
  }, [activeStep?.id]);
  return <div className="bg-gradient-to-br from-slate-50/50 to-slate-100/30 border border-slate-200/50 rounded-xl p-5 shadow-sm" data-id="element-547">
      {/* Compact Header with Progress */}
      <div className="flex items-center justify-between mb-4" data-id="element-548">
        <div className="flex items-center gap-3" data-id="element-549">
          <div className="relative flex-shrink-0" data-id="element-550">
            <div className="w-8 h-8 rounded-lg bg-white/80 border border-slate-200/50 flex items-center justify-center shadow-sm" data-id="element-551">
              {isThinking ? <Loader2 size={14} className="text-slate-500 animate-spin" data-id="element-552" /> : <Check size={14} className="text-emerald-600" data-id="element-553" />}
            </div>
          </div>
          <div data-id="element-554">
            <h3 className="text-sm font-semibold text-slate-700" data-id="element-555">
              {isThinking ? 'Thinking' : 'Complete'}
            </h3>
            <p className="text-xs text-slate-400" data-id="element-556">
              Step {completedSteps + (isThinking ? 1 : 0)} of {steps.length}
            </p>
          </div>
        </div>

        {/* Mini progress dots */}
        <div className="flex items-center gap-1.5" data-id="element-557">
          {steps.map((step, idx) => <div key={step.id} className={`h-1.5 rounded-full transition-all duration-300 ${step.status === 'complete' ? 'w-6 bg-slate-400' : step.status === 'active' ? 'w-8 bg-slate-600' : 'w-1.5 bg-slate-200'}`} data-id="element-558" />)}
        </div>
      </div>

      {/* Active Step - Large and Prominent */}
      <AnimatePresence mode="wait" data-id="element-559">
        {activeStep && <motion.div key={activeStep.id} initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }} className="bg-white/60 backdrop-blur-sm rounded-lg border border-slate-200/50 p-4 shadow-sm" data-id="element-560">
            {/* Step Header */}
            <div className="flex items-start gap-3 mb-3" data-id="element-561">
              <div className="flex-shrink-0 mt-0.5" data-id="element-562">
                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center" data-id="element-563">
                  <Loader2 size={12} className="text-slate-600 animate-spin" data-id="element-564" />
                </div>
              </div>

              <div className="flex-1 min-w-0" data-id="element-565">
                <div className="flex items-center gap-2 mb-1" data-id="element-566">
                  <h4 className="text-base font-semibold text-slate-800" data-id="element-567">
                    {activeStep.title}
                  </h4>
                  {activeStep.action && <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${getActionColor(activeStep.action)}`} data-id="element-568">
                      {getActionIcon(activeStep.action)}
                      <span className="capitalize" data-id="element-569">{activeStep.action}</span>
                    </span>}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-570">
                  {activeStep.description}
                </p>
              </div>

              {activeStep.duration && <div className="flex-shrink-0 text-xs text-slate-400 font-medium" data-id="element-571">
                  ~{activeStep.duration}s
                </div>}
            </div>

            {/* Progressive Sub-steps */}
            {activeStep.details && activeStep.details.length > 0 && <div className="pl-9 space-y-2 mt-3 pt-3 border-t border-slate-100/50" data-id="element-572">
                <AnimatePresence data-id="element-573">
                  {activeStep.details.map((detail, idx) => {
              const isVisible = visibleDetailIndices.includes(idx);
              const isLast = idx === activeStep.details!.length - 1;
              const isComplete = idx < visibleDetailIndices.length - 1;
              return <motion.div key={idx} initial={{
                opacity: 0,
                x: -10,
                height: 0
              }} animate={isVisible ? {
                opacity: 1,
                x: 0,
                height: 'auto'
              } : {
                opacity: 0,
                x: -10,
                height: 0
              }} transition={{
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1]
              }} className="overflow-hidden" data-id="element-574">
                        <div className="flex items-start gap-2.5" data-id="element-575">
                          {/* Status indicator for each detail */}
                          <div className="flex-shrink-0 mt-1" data-id="element-576">
                            {isComplete ? <div className="w-3 h-3 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-577">
                                <Check size={8} className="text-emerald-600" strokeWidth={3} data-id="element-578" />
                              </div> : isVisible && !isLast ? <div className="w-3 h-3 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-579">
                                <Loader2 size={8} className="text-blue-600 animate-spin" data-id="element-580" />
                              </div> : <div className="w-3 h-3 rounded-full border-2 border-slate-200 bg-white" data-id="element-581" />}
                          </div>

                          <p className={`text-xs leading-relaxed transition-colors ${isComplete ? 'text-slate-500' : 'text-slate-700 font-medium'}`} data-id="element-582">
                            {detail}
                          </p>
                        </div>
                      </motion.div>;
            })}
                </AnimatePresence>
              </div>}
          </motion.div>}
      </AnimatePresence>

      {/* Completion State */}
      {!isThinking && !activeStep && completedSteps === steps.length && <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="bg-emerald-50/50 border border-emerald-200/50 rounded-lg p-4 text-center" data-id="element-583">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-2" data-id="element-584">
            <Check size={18} className="text-emerald-600" strokeWidth={2.5} data-id="element-585" />
          </div>
          <p className="text-sm font-semibold text-emerald-900 mb-0.5" data-id="element-586">
            Analysis Complete
          </p>
          <p className="text-xs text-emerald-700" data-id="element-587">
            All {steps.length} steps processed successfully
          </p>
        </motion.div>}
    </div>;
}
// Example usage with mock data generator
export function generateMockReasoningSteps(): ReasoningStep[] {
  return [{
    id: 'step-1',
    title: 'Understanding requirements',
    description: 'Analyzing user request and gathering context',
    action: 'read',
    duration: 2,
    status: 'complete',
    details: ['User wants to add tabs to key insights', 'Need to separate insights from trade ideas', 'Modal functionality required for full view']
  }, {
    id: 'step-2',
    title: 'Reading Dashboard structure',
    description: 'Examining current implementation patterns',
    action: 'search',
    duration: 3,
    status: 'complete',
    details: ['Found Dashboard.tsx with view routing', 'OverviewView displays key insights', 'Using localStorage for persistence']
  }, {
    id: 'step-3',
    title: 'Planning component changes',
    description: 'Designing tab interface and modal system',
    duration: 4,
    status: 'complete',
    details: ['Add state for active tab (insights/trades)', 'Filter savedKeyPoints by type', 'Create modal with AnimatePresence', 'Add 3-dot menu with dropdown']
  }, {
    id: 'step-4',
    title: 'Implementing tabs',
    description: 'Adding tab navigation with Framer Motion',
    action: 'edit',
    duration: 5,
    status: 'active',
    details: ['Create tab buttons with active state', 'Add layoutId for smooth indicator', 'Filter display based on activeTab']
  }, {
    id: 'step-5',
    title: 'Adding modal functionality',
    description: 'Creating full-view modal for key insights',
    action: 'create',
    status: 'pending'
  }, {
    id: 'step-6',
    title: 'Implementing 3-dot menus',
    description: 'Adding action dropdown to all insights',
    action: 'create',
    status: 'pending'
  }];
}